import { AddressElement } from '@stripe/react-stripe-js';
import cn from 'classnames';
import { useFormikContext } from 'formik';
import pick from 'lodash/pick';
import { useCallback, useEffect, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { CSSTransition } from 'react-transition-group';

import FormField from 'components/common/FormField';
import Heading from 'components/common/Heading';
import Text from 'components/common/Text';
import AutocompleteGoogle from 'components/signUpFunnel/checkout/AutocompleteGoogle';
import Sms from 'components/signUpFunnel/checkout/Sms';
import { TEMPO } from 'context/BrandContext';
import localStorage from 'helpers/localStorage';
import useBrand from 'hooks/useBrand';
import usePartnershipQuery from 'hooks/usePartnershipQuery';
import useScrollToFormikError from 'hooks/useScrollToFormikError';
import { INTRO_SMS_OPT_IN, STRIPE, PROVIDER_APPLE } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import { updateBox } from 'services/RequestService/endpoints/signUp';
import States from 'utilities/states';

const textColorMapping = {
  standard: 'text-green-100',
  family: 'text-green-100',
  tempo: 'text-tempo-blue-90',
};

const AddressFields = ({
  children,
  deliveryWeeks,
  disableSubmit,
  handleZipCodeChange,
  mobileSignUp,
  setAddressDetails,
  setBox,
  setDisableSubmit,
  setToastMessage,
  showPaymentOptions,
  user,
  zipCodeDeliverability,
  refreshDeliveryOptions,
}) => {
  useScrollToFormikError();

  const isTempo = useBrand(TEMPO);

  const {
    touched,
    setFieldError,
    errors,
    setTouched,
    resetForm,
    setFieldValue,
    handleChange,
    values,
    isSubmitting,
  } = useFormikContext();
  const transitionRef = useRef();
  const valuesRef = useRef(values);
  const { data: partnershipData } = usePartnershipQuery(user?.partnerReferrer);

  const userOptedInToSmsOnIntro = localStorage.read(INTRO_SMS_OPT_IN) === true;
  const appleSignIn = mobileSignUp && user.provider === PROVIDER_APPLE;
  const showBillingAddress = !appleSignIn && values.vendor === STRIPE;
  const shippingAddressOptions = {
    allowedCountries: ['US'],
    blockPoBox: true,
    defaultValues: {
      name: user?.name || '',
      address: {
        line1: user?.address?.streetAddress || '',
        line2: user?.address?.streetAddress2 || '',
        city: user?.signupCity || '',
        state: user?.signupState || '',
        postal_code: values.zipCode,
        country: 'US',
      },
    },
    fields: {
      phone: 'never',
    },
    mode: 'shipping',
  };

  useEffect(() => {
    setFieldValue('city', user?.signupCity || '');
    setFieldValue('state', user?.signupState || '');
  }, [setFieldValue, user?.signupCity, user?.signupState, user?.signupZip]);

  useEffect(() => {
    setFieldValue('fullName', user?.name || '');
    setFieldValue('streetAddress', user?.address?.streetAddress || '');
    setFieldValue('apartmentNumber', user?.address?.streetAddress2 || '');
    setFieldValue('phoneNumber', user?.phone || '');
    setFieldValue('city', user?.signupCity || '');
    setFieldValue('state', user?.signupState || '');
    setFieldValue('zipCode', user?.signupZip || '');
    setFieldValue('vendor', showPaymentOptions ? user?.vendor : '');
  }, [
    setFieldValue,
    showPaymentOptions,
    user?.address?.streetAddress,
    user?.address?.streetAddress2,
    user?.name,
    user?.phone,
    user?.signupCity,
    user?.signupState,
    user?.signupZip,
    user?.vendor,
  ]);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    if (values.zipCode && values.zipCode.length === 5) {
      refreshDeliveryOptions(valuesRef.current, { resetForm });
    }
  }, [values.zipCode, refreshDeliveryOptions, resetForm]);

  const handleFirstDeliveryDateChange = useCallback(
    async date => {
      setDisableSubmit(true);
      try {
        const response = await updateBox({
          params: {
            updatedFirstDeliveryDate: date,
          },
        });
        if (response.ok) {
          setBox(box => ({ ...box, ...pick(response.box, ['editingCutoff', 'deliveryDate']) }));
        } else {
          setFieldError('firstDeliveryDate', response.error);
          setFieldValue('firstDeliveryDate', '');
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      } finally {
        setDisableSubmit(false);
      }
    },
    [setBox, setDisableSubmit, setFieldError, setFieldValue]
  );

  const onStripeAddressFormChange = useCallback(
    event => {
      setFieldValue('fullName', event.value.name || '');
      setFieldValue('streetAddress', event.value.address.line1 || '');
      setFieldValue('apartmentNumber', event.value.address.line2 || '');
      setFieldValue('city', event.value.address.city || '');
      setFieldValue('state', event.value.address.state || '');
      setFieldValue('zipCode', event.value.address.postal_code || '');
    },
    [setFieldValue]
  );

  const getDeliveryDateHelperText = useCallback(
    () => (
      <>
        {partnershipData?.partnership?.disclaimer && (
          <span className={cn('block pt-2', textColorMapping[user?.mealPlanSlug] || '')}>
            {partnershipData.partnership.disclaimer}
          </span>
        )}
        <span className="block">You can change your preferred weekly delivery day any time.</span>
      </>
    ),
    [user?.mealPlanSlug, partnershipData]
  );

  return (
    <div className="mx-auto">
      <section
        className={cn(
          'relative flex flex-col gap-4 border',
          isTempo ? 'border-charcoal-60' : 'border-cream-120'
        )}
      >
        <Heading brandStylesOverrides={isTempo ? 'h3' : 'h4'} className="mb-0">
          Shipping Address
        </Heading>
        {children}
        {showBillingAddress ? (
          <>
            <AddressElement options={shippingAddressOptions} onChange={onStripeAddressFormChange} />
            <Text brandStylesOverrides="textSm text-light" className="text-charcoal-90">
              We cannot ship to P.O. boxes.
            </Text>
          </>
        ) : (
          <>
            <FormField
              label="Full Name"
              labelBrandStylesOverrides="textMd"
              name="fullName"
              onBlur={() => setTouched({ fullName: true })}
              error={touched.fullName && errors.fullName}
              variant="refreshed"
            />
            <AutocompleteGoogle
              id="streetAddress"
              name="streetAddress"
              setAddressDetails={setAddressDetails}
              label="Address Line 1"
              labelBrandStylesOverrides="textMd"
              onBlur={() => setTouched({ streetAddress: true })}
              error={touched.streetAddress && errors.streetAddress}
              helperText="We cannot ship to P.O. boxes."
              variant="refreshed"
            />
            <FormField
              label="Address Line 2"
              labelBrandStylesOverrides="textMd"
              name="apartmentNumber"
              onBlur={() => setTouched({ apartmentNumber: true })}
              error={touched.apartmentNumber && errors.apartmentNumber}
              variant="refreshed"
            />
            <FormField
              label="City"
              labelBrandStylesOverrides="textMd"
              name="city"
              onBlur={() => setTouched({ city: true })}
              error={touched.city && errors.city}
              variant="refreshed"
            />
            <div className="flex space-x-6 bp2:space-x-8">
              <div className="relative w-full bp2:mb-0">
                <FormField
                  label="State"
                  labelBrandStylesOverrides="textMd"
                  name="state"
                  component="select"
                  error={touched.state && errors.state}
                  options={States.map(state => ({ key: state.name, value: state.abbreviation }))}
                  onChange={state =>
                    handleChange({
                      target: {
                        name: 'state',
                        value: state,
                      },
                    })
                  }
                  variant="refreshed"
                />
              </div>
              <div
                className={cn('relative w-full', {
                  'whitespace-nowrap': errors.zipCode || !zipCodeDeliverability.isDeliverable,
                })}
              >
                <FormField
                  label="ZIP Code"
                  labelBrandStylesOverrides="textMd"
                  name="zipCode"
                  inputClass="w-full"
                  onBlur={e => {
                    handleZipCodeChange(e.target.value);
                    setTouched({ zipCode: true });
                  }}
                  error={
                    touched.zipCode && (errors.zipCode || !zipCodeDeliverability.isDeliverable)
                      ? zipCodeDeliverability.message || errors.zipCode
                      : null
                  }
                  maxLength={5}
                  inputMode="numeric"
                  variant="refreshed"
                />
                <div role="status">
                  <CSSTransition
                    in={zipCodeDeliverability.isDeliverable}
                    timeout={200}
                    classNames="fadeInOut"
                    unmountOnExit
                    nodeRef={transitionRef}
                  >
                    <div ref={transitionRef}>
                      <Text
                        brandStylesOverrides="textSm text-primary"
                        className="absolute bottom-1 whitespace-nowrap"
                      >
                        {zipCodeDeliverability.message}
                      </Text>
                    </div>
                  </CSSTransition>
                </div>
              </div>
            </div>
          </>
        )}
        <FormField
          label="Phone Number"
          labelBrandStylesOverrides="textMd"
          name="phoneNumber"
          onBlur={() => setTouched({ phoneNumber: true })}
          error={touched.phoneNumber && errors.phoneNumber}
          inputClass="w-full"
          inputMode="numeric"
          helperText="We use this to create your account. We won't send you any unwanted text messages."
          variant="refreshed"
          errorContainerClass="min-h-1"
        />
        <div className="relative mb-1 w-full">
          {deliveryWeeks.length > 0 ? (
            <FormField
              label="First Delivery Date"
              labelBrandStylesOverrides="textMd"
              name="firstDeliveryDate"
              inputClass="py-0"
              component="select"
              type={null}
              error={errors.firstDeliveryDate}
              disabled={disableSubmit || isSubmitting}
              onChange={value => {
                handleChange({
                  target: {
                    name: 'firstDeliveryDate',
                    value,
                  },
                });
                handleFirstDeliveryDateChange(value);
              }}
              helperText={getDeliveryDateHelperText()}
              options={deliveryWeeks.flatMap(week => {
                if (week.firstDeliveryDays) {
                  return week.firstDeliveryDays.map(day => {
                    return {
                      key: day.label,
                      value: day.value,
                      group: week.menuGroup,
                    };
                  });
                } else {
                  return {
                    key: week.label,
                    value: week.value,
                    disabled: true,
                  };
                }
              })}
              variant="refreshed"
              errorContainerClass="min-h-0"
            />
          ) : (
            <Skeleton count={1} className="h-12" />
          )}
        </div>
        {!userOptedInToSmsOnIntro && (
          <>
            <hr className="my-0 border-b border-solid border-charcoal-60" />
            <Heading brandStylesOverrides="h5" className="mb-0">
              SMS Opt-In
            </Heading>
            <Sms setToastMessage={setToastMessage} />
            <hr className="mt-0 border-b border-solid border-charcoal-60" />
          </>
        )}
      </section>
    </div>
  );
};

AddressFields.propTypes = {
  children: PropTypes.node,
  user: PropTypes.object.isRequired,
  deliveryWeeks: PropTypes.array.isRequired,
  handleZipCodeChange: PropTypes.func.isRequired,
  zipCodeDeliverability: PropTypes.object.isRequired,
  setAddressDetails: PropTypes.func.isRequired,
  showPaymentOptions: PropTypes.bool.isRequired,
  setBox: PropTypes.func.isRequired,
  disableSubmit: PropTypes.bool.isRequired,
  setDisableSubmit: PropTypes.func.isRequired,
  setToastMessage: PropTypes.func.isRequired,
  refreshDeliveryOptions: PropTypes.func.isRequired,
  mobileSignUp: PropTypes.bool.isRequired,
};

export default AddressFields;
